import { create } from 'zustand';
import { dtpApi } from './dtpApi';
import { loadZendeskScript, waitForZE, ZENDESK_SCRIPT_ID } from '../components/support/ZendeskWidget';
import { track } from './analytics';

export type SupportChatPlacement = 'launcher' | 'help_page' | 'home' | 'messages_banner' | 'ava';

/**
 * Zendesk support chat for the patient portal (orders, shipping, payments,
 * the account). Clinical questions go to the care team instead (Messages).
 *
 * The portal shows its own "Chat with support" buttons rather than Zendesk's
 * floating bubble, which would sit on the phone tab bar: the messenger is kept
 * hidden and only shown while open.
 */

type Status = 'idle' | 'loading' | 'ready' | 'unavailable';

export const usePatientSupportChat = create<{ status: Status; unread: number; sessionKey: string | null }>(() => ({
  status: 'idle',
  unread: 0,
  sessionKey: null,
}));

const set = usePatientSupportChat.setState;
let starting: Promise<void> | null = null;

/** Load the messenger for this patient session. Idempotent per session. */
export function startPatientSupportChat(sessionKey: string): Promise<void> {
  const current = usePatientSupportChat.getState();
  if (current.sessionKey === sessionKey && current.status !== 'idle') return starting ?? Promise.resolve();
  set({ status: 'loading', unread: 0, sessionKey });
  const stillCurrent = () => usePatientSupportChat.getState().sessionKey === sessionKey;

  starting = (async () => {
    try {
      const identity = await dtpApi.getSupportIdentity();
      if (!stillCurrent()) return;
      if (!identity.available || !identity.widgetKey || !identity.token) { set({ status: 'unavailable' }); return; }

      // A messenger already on the page under another key (a staff session
      // earlier in this tab) can't be swapped without a reload, and must not
      // carry the patient's conversation.
      const existing = document.getElementById(ZENDESK_SCRIPT_ID) as HTMLScriptElement | null;
      if (existing && !existing.src.includes(`key=${encodeURIComponent(identity.widgetKey)}`)) { set({ status: 'unavailable' }); return; }

      await loadZendeskScript(identity.widgetKey);
      const zE = await waitForZE();
      if (!zE || !stillCurrent()) { if (stillCurrent()) set({ status: 'unavailable' }); return; }

      zE('messenger', 'hide');
      zE('messenger:on', 'close', () => zE('messenger', 'hide'));
      zE('messenger:on', 'unreadMessages', (count: number) => { if (stillCurrent()) set({ unread: count || 0 }); });
      if (identity.tags?.length) zE('messenger:set', 'conversationTags', identity.tags);
      // Zendesk asks again whenever the JWT expires (1h), so sign a fresh one each time.
      zE('messenger', 'loginUser', async (callback: (jwt: string) => void) => {
        try {
          const fresh = await dtpApi.getSupportIdentity();
          callback(fresh.available && fresh.token ? fresh.token : identity.token!);
        } catch {
          callback(identity.token!);
        }
      });
      set({ status: 'ready' });
    } catch {
      if (stillCurrent()) set({ status: 'unavailable' });
    }
  })();
  return starting;
}

/** Open the support conversation. False when the messenger isn't loaded. */
export function openPatientSupportChat(placement?: SupportChatPlacement): boolean {
  const zE = window.zE;
  if (usePatientSupportChat.getState().status !== 'ready' || typeof zE !== 'function') return false;
  zE('messenger', 'show');
  zE('messenger', 'open');
  track('support_chat_opened', { placement });
  return true;
}

/** Sign the patient out of the messenger, e.g. on portal sign-out. */
export function endPatientSupportChat() {
  const zE = window.zE;
  if (usePatientSupportChat.getState().status === 'ready' && typeof zE === 'function') {
    try { zE('messenger', 'logoutUser'); zE('messenger', 'hide'); } catch { /* the widget is best-effort */ }
  }
  starting = null;
  set({ status: 'idle', unread: 0, sessionKey: null });
}
